import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import {
  listCreditAllocations,
  listCreditBonusTiers,
  listCreditTransactions,
  readCreditSpending,
  readCredits,
  type CreditSpendingQuery,
  type CreditTransactionsQuery,
} from '@/api/billing/credits'
import {
  readAutoTopUp,
  updateAutoTopUp,
  type AutoTopUpView,
  type SetAutoTopUpRequest,
} from '@/api/billing/autoTopUp'
import {
  listBillingDocuments,
  readBillingDocumentPdf,
  type BillingDocumentsQuery,
} from '@/api/billing/documents'
import {
  readBillingProfile,
  updateBillingProfile,
  type BillingProfileView,
  type SetBillingProfileRequest,
} from '@/api/billing/profile'
import {
  cancelCreditPurchase,
  listCreditPurchases,
  listCreditPurchaseRefunds,
  openCreditPayment,
  openCreditPurchase,
  readCreditPurchase,
  reconcileCreditPurchase,
  type CreditPurchasesQuery,
} from '@/api/billing/purchases'
import {
  listRecurringCharges,
  listUsageDays,
  listUsagePeriods,
  type UsageDaysQuery,
  type UsagePeriodsQuery,
} from '@/api/usage/usage'
import { useSessionStore } from '@/apps/session/stores/session'

// The money reads of the organization the URL names. No organization in any key below: the
// session guard clears the whole query cache when the organization changes, so every entry
// already belongs to the one in the URL.
//
// **Every route behind these takes `tenancy.billing.manage`, the reads included.** So every
// composable here is enabled on that key and on nothing else: a member without it sends no
// request at all, rather than sending one that answers 403. That is the gate a screen relies on —
// `canManageBilling()` is the same reading, for a `v-if`.
//
// **The money reads carry a second condition, and the two meters deliberately do not** (D17). On
// a platform that sells no credits there is no `credit_balances` row and there never will be, so
// a balance, its buckets, the bonus tiers, the journal, the top-ups, the invoices, the invoiced
// identity and the monthly fees are reads about something that does not exist — `whenSelling` is
// what stops them leaving. What `usage_days` and `usage_periods` hold is written under both
// regimes and is that tenant's own consumption, so `useUsageDays` and `useUsagePeriods` stay on
// `whenPermitted` and feed the one money screen that regime still reaches.

/** The wallet's keys. */
export const walletKeys = {
  all: ['billing'] as const,
  balance: () => ['billing', 'balance'] as const,
  allocations: () => ['billing', 'allocations'] as const,
  bonusTiers: () => ['billing', 'bonus-tiers'] as const,
  transactions: (query: CreditTransactionsQuery) => ['billing', 'transactions', query] as const,
  spending: (query: CreditSpendingQuery) => ['billing', 'spending', query] as const,
  profile: () => ['billing', 'profile'] as const,
  documents: (query: BillingDocumentsQuery) => ['billing', 'documents', query] as const,
  purchases: (query: CreditPurchasesQuery) => ['billing', 'purchases', query] as const,
  purchase: (purchaseId: string) => ['billing', 'purchases', 'one', purchaseId] as const,
  purchaseRefunds: (purchaseId: string) => ['billing', 'purchases', 'one', purchaseId, 'refunds'] as const,
  autoTopUp: () => ['billing', 'auto-top-up'] as const,
}

/** The measurements' keys. */
export const usageKeys = {
  all: ['usage'] as const,
  days: (query: UsageDaysQuery) => ['usage', 'days', query] as const,
  periods: (query: UsagePeriodsQuery) => ['usage', 'periods', query] as const,
  recurringCharges: () => ['usage', 'recurring-charges'] as const,
}

interface ReadOptions {
  enabled?: MaybeRefOrGetter<boolean>
}

/**
 * Whether the caller may read this organization's money at all.
 *
 * **Keyed on the permission, never on a role name** — the effective set `GET /access` answered for
 * the organization in the URL, which the session guard filled before any view of it mounted. It
 * spares a screen that could only fail; the backend refuses every one of these reads regardless.
 */
export function canManageBilling(): boolean {
  return useSessionStore().can('tenancy.billing.manage')
}

/**
 * Whether this organization has a wallet to show at all — the permission **and** its platform
 * selling credits (`GET /access`'s `org.sells_credits`, D17).
 *
 * [`canManageBilling`]'s counterpart for anything that draws a balance, a price in credits or a
 * top-up, and the reading a `v-if` outside this module makes. The two are kept apart because the
 * consumption screen wants the first without the second.
 */
export function canSeeWallet(): boolean {
  const session = useSessionStore()
  return session.can('tenancy.billing.manage') && session.sellsCredits()
}

/**
 * The store is resolved **here**, in the composable's own setup, and read inside the computed —
 * not resolved inside it. A `useStore()` called from a getter depends on Pinia's active instance
 * at whatever moment the getter happens to re-run, which is not a place the composition root
 * controls.
 */
function whenPermitted(options: ReadOptions) {
  const session = useSessionStore()
  return computed(
    () => session.can('tenancy.billing.manage') && toValue(options.enabled) !== false,
  )
}

/**
 * [`whenPermitted`] and the credit regime — what every **wallet** read here is enabled on.
 *
 * A platform that sells no credits — billed by contract, or not provisioned — opens no wallet,
 * so these reads would answer a 404 or an empty page and a screen would have to decide what an
 * absent balance means. It must not have to: a zero on a selling platform means *top up*, and
 * the two are not the same picture.
 */
function whenSelling(options: ReadOptions) {
  const session = useSessionStore()
  const permitted = whenPermitted(options)
  return computed(() => permitted.value && session.sellsCredits())
}

// ---------------------------------------------------------------------------- the wallet ----

/**
 * The wallet: the balance and its two halves, the next free grant, the runway, the lifetime
 * totals, the Terms of Sale notice.
 *
 * A 404 here is not an error to report as one — it is an organization whose platform declares no
 * credit policy, and therefore has no wallet. A screen reads `isNotFound` off the error's status.
 */
export function useWallet(options: ReadOptions = {}) {
  return useQuery({
    queryKey: walletKeys.balance(),
    queryFn: ({ signal }) => readCredits(signal),
    enabled: whenSelling(options),
    staleTime: 60_000,
    // A wallet that has no policy row will not grow one while the screen is open.
    retry: false,
  })
}

/** The live buckets, soonest to lapse first. */
export function useCreditAllocations(options: ReadOptions = {}) {
  return useQuery({
    queryKey: walletKeys.allocations(),
    queryFn: ({ signal }) => listCreditAllocations(signal),
    enabled: whenSelling(options),
    retry: false,
  })
}

/**
 * The amounts the platform offers and what each earns.
 *
 * **The served tiers, never the catalogue's copy.** `RELOAD_BONUS_TIERS` in the generated pricing
 * file is a new platform's default; announcing a bonus from it would announce one the wallet may
 * not receive.
 */
export function useCreditBonusTiers(options: ReadOptions = {}) {
  return useQuery({
    queryKey: walletKeys.bonusTiers(),
    queryFn: ({ signal }) => listCreditBonusTiers(signal),
    enabled: whenSelling(options),
    staleTime: Infinity,
    retry: false,
  })
}

/**
 * The journal, walked page by page. Newest first, cursor-paged and with no total — so the screen
 * offers *load more* rather than page numbers, which is what a cursor can honestly serve.
 */
export function useCreditTransactions(
  query: MaybeRefOrGetter<CreditTransactionsQuery> = {},
  options: ReadOptions = {},
) {
  return useInfiniteQuery({
    queryKey: computed(() => walletKeys.transactions(toValue(query))),
    queryFn: ({ pageParam, signal }) =>
      listCreditTransactions({ ...toValue(query), after: pageParam ?? undefined }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next ?? null,
    enabled: whenSelling(options),
    retry: false,
  })
}

/** What the wallet spent over a range, by UTC day and service (`readCreditSpending`). */
export function useCreditSpending(
  query: MaybeRefOrGetter<CreditSpendingQuery> = {},
  options: ReadOptions = {},
) {
  return useQuery({
    queryKey: computed(() => walletKeys.spending(toValue(query))),
    queryFn: ({ signal }) => readCreditSpending(toValue(query), signal),
    enabled: whenSelling(options),
    staleTime: 60_000,
    retry: false,
  })
}

// --------------------------------------------------------------------------- buying credits ----

/**
 * The top-up history, newest first, walked.
 *
 * **This is where a top-up that went wrong is readable**, and it is the reason the screen exists:
 * a purchase is opened before anything is charged, so an attempt that failed, a browser that was
 * closed and an event that never arrived all leave a row here rather than nothing at all.
 */
export function useCreditPurchases(
  query: MaybeRefOrGetter<CreditPurchasesQuery> = {},
  options: ReadOptions = {},
) {
  return useInfiniteQuery({
    queryKey: computed(() => walletKeys.purchases(toValue(query))),
    queryFn: ({ pageParam, signal }) =>
      listCreditPurchases({ ...toValue(query), after: pageParam ?? undefined }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next ?? null,
    enabled: whenSelling(options),
    retry: false,
  })
}

/** One top-up. */
export function useCreditPurchase(
  purchaseId: MaybeRefOrGetter<string>,
  options: ReadOptions = {},
) {
  const permitted = whenSelling(options)
  return useQuery({
    queryKey: computed(() => walletKeys.purchase(toValue(purchaseId))),
    queryFn: ({ signal }) => readCreditPurchase(toValue(purchaseId), signal),
    enabled: computed(() => permitted.value && toValue(purchaseId) !== ''),
    retry: false,
  })
}

/** What was given back on one top-up. Empty on all but the few an operator refunded. */
export function useCreditPurchaseRefunds(
  purchaseId: MaybeRefOrGetter<string>,
  options: ReadOptions = {},
) {
  const permitted = whenSelling(options)
  return useQuery({
    queryKey: computed(() => walletKeys.purchaseRefunds(toValue(purchaseId))),
    queryFn: ({ signal }) => listCreditPurchaseRefunds(toValue(purchaseId), signal),
    enabled: computed(() => permitted.value && toValue(purchaseId) !== ''),
    retry: false,
  })
}

/**
 * Open a top-up.
 *
 * **The key is the caller's and is minted once per pay gesture**, not once per call: a retry after
 * a timeout sends the same one, and the backend answers the row it already wrote without dialling
 * the invoicing service a second time. `retry: false` is therefore not caution about duplicates —
 * it is that a failure at the invoicing service is not retryable on our side, so the person is
 * shown what came back instead of the same call being made again behind their back.
 */
export function useOpenCreditPurchase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { amountExclTaxCents: number; idempotencyKey: string }) =>
      openCreditPurchase({ amount_excl_tax_cents: input.amountExclTaxCents }, input.idempotencyKey),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: walletKeys.all })
    },
  })
}

/**
 * Open the payment attempt.
 *
 * **A new key every time**, because a declined attempt is terminal at the invoicing service and
 * the new attempt cancels the superseded one. `crypto.randomUUID` rather than the purchase's own
 * uid for exactly that reason.
 */
export function useOpenCreditPayment() {
  return useMutation({
    mutationFn: (input: { purchaseId: string; saveInstrument?: boolean }) =>
      openCreditPayment(input.purchaseId, crypto.randomUUID(), {
        save_instrument: input.saveInstrument ?? false,
      }),
    retry: false,
  })
}

/**
 * Settle a top-up the invoicing service has not announced.
 *
 * **The one call in this domain that is safe to make again and again**: both halves are the event
 * drain's own statements on the same rows, and the journal line is unique per sale, so the wallet
 * is credited exactly once however many times this runs. It is what a screen offers against a
 * purchase stuck at `open` — the alternative being a spinner that resolves nothing.
 */
export function useReconcileCreditPurchase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (purchaseId: string) => reconcileCreditPurchase(purchaseId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: walletKeys.all })
    },
  })
}

/**
 * Cancel an open, unpaid top-up (`billing-ui.md` U8). Idempotent on the server, so a double click
 * is one cancellation; `retry: false` because a `409` — the top-up was paid meanwhile — is an
 * answer to show, not a failure to repeat. The wallet is invalidated either way: a refusal may
 * mean the cancellation found the payment and delivered its credits.
 */
export function useCancelCreditPurchase() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (purchaseId: string) => cancelCreditPurchase(purchaseId),
    retry: false,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: walletKeys.all })
    },
  })
}

// ----------------------------------------------------------------- the automatic top-up ----

/**
 * The automatic top-up: whether it is on, under what threshold and for how much, the daily caps,
 * the consecutive failures and the consent it rests on.
 */
export function useAutoTopUp(options: ReadOptions = {}) {
  return useQuery({
    queryKey: walletKeys.autoTopUp(),
    queryFn: ({ signal }) => readAutoTopUp(signal),
    enabled: whenSelling(options),
    retry: false,
  })
}

/**
 * Set it whole, consent included. The answer replaces the cached arrangement, and the wallet's other
 * reads are left alone: switching the top-up on or off moves no money.
 */
export function useUpdateAutoTopUp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: SetAutoTopUpRequest) => updateAutoTopUp(body),
    retry: false,
    onSuccess: (arrangement: AutoTopUpView) => {
      queryClient.setQueryData(walletKeys.autoTopUp(), arrangement)
    },
  })
}

// -------------------------------------------------------------------- the billing identity ----

/**
 * Who the organization is invoiced as. Every field is `null` until it has a customer at the
 * invoicing service — which is a state and not an error.
 *
 * A deployment naming no invoicing service answers `503`, so this read fails where the wallet
 * reads fine: the screen says the service is unavailable rather than showing an empty form that
 * cannot be saved.
 */
export function useBillingProfile(options: ReadOptions = {}) {
  return useQuery({
    queryKey: walletKeys.profile(),
    queryFn: ({ signal }) => readBillingProfile(signal),
    enabled: whenSelling(options),
    staleTime: 30_000,
    retry: false,
  })
}

/** Save it whole. The answer replaces the cached profile rather than invalidating it. */
export function useUpdateBillingProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: SetBillingProfileRequest) => updateBillingProfile(body),
    onSuccess: (profile: BillingProfileView) => {
      queryClient.setQueryData(walletKeys.profile(), profile)
    },
  })
}

// -------------------------------------------------------------------------- the documents ----

/**
 * The invoices and credit notes, walked. The cursor is the invoicing service's own and travels
 * through unchanged.
 */
export function useBillingDocuments(
  query: MaybeRefOrGetter<BillingDocumentsQuery> = {},
  options: ReadOptions = {},
) {
  return useInfiniteQuery({
    queryKey: computed(() => walletKeys.documents(toValue(query))),
    queryFn: ({ pageParam, signal }) =>
      listBillingDocuments({ ...toValue(query), after: pageParam ?? undefined }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next ?? null,
    enabled: whenSelling(options),
    retry: false,
  })
}

/**
 * Save a document's PDF to disk.
 *
 * Fetched rather than linked, because the call carries `X-Org-Id`; the blob is released on the
 * next tick rather than the same one, which Firefox and Safari cancel the download over.
 */
export async function downloadBillingDocumentPdf(documentId: string, fallbackName: string): Promise<void> {
  const { blob, filename } = await readBillingDocumentPdf(documentId)
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename ?? `${fallbackName}.pdf`
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
}

// ----------------------------------------------------------------------- the measurements ----

/** A page of metered days, newest first, walked. */
export function useUsageDays(
  query: MaybeRefOrGetter<UsageDaysQuery> = {},
  options: ReadOptions = {},
) {
  return useInfiniteQuery({
    queryKey: computed(() => usageKeys.days(toValue(query))),
    queryFn: ({ pageParam, signal }) =>
      listUsageDays({ ...toValue(query), after: pageParam ?? undefined }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next ?? null,
    enabled: whenPermitted(options),
    retry: false,
  })
}

/** The allowance windows, newest first. Bounded rather than paged: a period is a month per meter. */
export function useUsagePeriods(
  query: MaybeRefOrGetter<UsagePeriodsQuery> = {},
  options: ReadOptions = {},
) {
  return useQuery({
    queryKey: computed(() => usageKeys.periods(toValue(query))),
    queryFn: ({ signal }) => listUsagePeriods(toValue(query), signal),
    enabled: whenPermitted(options),
    retry: false,
  })
}

/**
 * The monthly fees, with their dunning state.
 *
 * **One read where there used to be one per channel plus one per channel's domains.** The server
 * holds the rows it bills, so nothing here derives a fee from a price list and a resource list,
 * and an exempt or grandfathered channel is simply absent instead of having to be filtered out.
 */
export function useRecurringCharges(options: ReadOptions = {}) {
  return useQuery({
    queryKey: usageKeys.recurringCharges(),
    queryFn: ({ signal }) => listRecurringCharges(signal),
    enabled: whenSelling(options),
    staleTime: 60_000,
    retry: false,
  })
}
